import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowSquareOut,
  CheckCircle,
  Database,
  MagnifyingGlass,
  PauseCircle,
  Plus,
  ShieldCheck,
  X,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";


const fieldClass =
  "h-11 w-full border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const emptyForm = {
  name: "",
  category: "other",
  official_url: "",
  country: "Portugal",
  import_method: "manual",
  status: "under_review",
  verification_status: "unverified",
  priority: 100,
  description: "",
  legal_notes: "",
};

const statusKey = {
  active: "active",
  paused: "paused",
  under_review: "underReview",
  disabled: "disabled",
};

function Stat({ icon: Icon, label, value, gold = false }) {
  return (
    <div className="flex items-center gap-4 border border-slate-200 bg-white p-5">
      <div
        className={`flex h-11 w-11 items-center justify-center ${
          gold
            ? "bg-[#C5A059]/10 text-[#9A7432]"
            : "bg-[#0B2545]/[0.07] text-[#0B2545]"
        }`}
      >
        <Icon size={22} />
      </div>
      <div>
        <div className="font-serif text-2xl text-[#0B2545]">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function SourceForm({ source, meta, onClose, onSave, saving, t }) {
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    ...(source || {}),
  }));

  const set = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const submit = (event) => {
    event.preventDefault();
    onSave({
      ...form,
      priority: Number(form.priority || 0),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#07182B]/70 p-4 backdrop-blur-sm">
      <form
        onSubmit={submit}
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto bg-[#F8F9FA] shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-slate-200 bg-white px-6 py-5">
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A77E35]">
              Panatlantik Intelligence
            </p>
            <h2 className="font-serif text-2xl text-[#0B2545]">
              {source
                ? t("bankOpportunities.sourceRegistry.edit")
                : t("bankOpportunities.sourceRegistry.add")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 transition-colors hover:text-[#0B2545]"
            aria-label={t("bankOpportunities.sourceRegistry.cancel")}
          >
            <X size={22} />
          </button>
        </div>

        <div className="grid gap-5 p-6 md:grid-cols-2">
          <label className="space-y-2 md:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("bankOpportunities.sourceRegistry.name")}
            </span>
            <Input
              required
              value={form.name}
              onChange={(event) => set("name", event.target.value)}
              className="h-11 rounded-none"
            />
          </label>

          <label className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("bankOpportunities.sourceRegistry.category")}
            </span>
            <select
              value={form.category}
              onChange={(event) => set("category", event.target.value)}
              className={fieldClass}
            >
              {(meta?.categories || []).map((category) => (
                <option key={category} value={category}>
                  {t(`bankOpportunities.sourceRegistry.categories.${category}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("bankOpportunities.sourceRegistry.status")}
            </span>
            <select
              value={form.status}
              onChange={(event) => set("status", event.target.value)}
              className={fieldClass}
            >
              {(meta?.statuses || []).map((status) => (
                <option key={status} value={status}>
                  {t(
                    `bankOpportunities.sourceRegistry.${
                      statusKey[status] || status
                    }`
                  )}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-2 md:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("bankOpportunities.sourceRegistry.officialUrl")}
            </span>
            <Input
              type="url"
              value={form.official_url || ""}
              onChange={(event) => set("official_url", event.target.value)}
              placeholder="https://"
              className="h-11 rounded-none"
            />
          </label>

          <label className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("bankOpportunities.sourceRegistry.country")}
            </span>
            <Input
              value={form.country || ""}
              onChange={(event) => set("country", event.target.value)}
              className="h-11 rounded-none"
            />
          </label>

          <label className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("bankOpportunities.sourceRegistry.priority")}
            </span>
            <Input
              type="number"
              min="0"
              value={form.priority}
              onChange={(event) => set("priority", event.target.value)}
              className="h-11 rounded-none"
            />
          </label>

          <label className="space-y-2 md:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("bankOpportunities.sourceRegistry.description")}
            </span>
            <textarea
              rows="4"
              value={form.description || ""}
              onChange={(event) => set("description", event.target.value)}
              className={`${fieldClass} h-auto py-3`}
            />
          </label>

          <label className="space-y-2 md:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("bankOpportunities.sourceRegistry.legalNotes")}
            </span>
            <textarea
              rows="3"
              value={form.legal_notes || ""}
              onChange={(event) => set("legal_notes", event.target.value)}
              className={`${fieldClass} h-auto py-3`}
            />
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 bg-white px-6 py-5">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="rounded-none"
          >
            {t("bankOpportunities.sourceRegistry.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="rounded-none bg-[#0B2545] px-6 hover:bg-[#12345c]"
          >
            {saving
              ? t("bankOpportunities.sourceRegistry.saving")
              : t("bankOpportunities.sourceRegistry.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function BankSources() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const params = {};
  if (search) params.q = search;
  if (category !== "all") params.category = category;
  if (status !== "all") params.status = status;

  const { data: sources = [], isLoading } = useQuery({
    queryKey: ["bank-opportunity-sources", params],
    queryFn: async () =>
      (await api.get("/bank-opportunity-sources", { params })).data,
  });

  const { data: meta } = useQuery({
    queryKey: ["bank-opportunity-source-meta"],
    queryFn: async () =>
      (await api.get("/bank-opportunity-sources/meta")).data,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      if (editing?.id) {
        return (await api.put(`/bank-opportunity-sources/${editing.id}`, payload))
          .data;
      }
      return (await api.post("/bank-opportunity-sources", payload)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bank-opportunity-sources"],
      });
      setShowForm(false);
      setEditing(null);
      toast.success(t("bankOpportunities.sourceRegistry.save"));
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.detail?.message ||
          error?.response?.data?.detail ||
          "Unable to save source"
      );
    },
  });

  const totals = useMemo(
    () => ({
      total: sources.length,
      active: sources.filter((source) => source.status === "active").length,
      official: sources.filter((source) => source.is_official).length,
    }),
    [sources]
  );

  const formatDate = (value) =>
    value
      ? new Intl.DateTimeFormat(i18n.resolvedLanguage || "en", {
          dateStyle: "medium",
        }).format(new Date(value))
      : t("bankOpportunities.sourceRegistry.neverChecked");

  const openEdit = (source) => {
    if (user?.role !== "admin") return;
    setEditing(source);
    setShowForm(true);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-7 p-5 md:p-8">
      <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <button
            onClick={() => navigate("/bank-opportunities")}
            className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-[#0B2545]"
          >
            <ArrowLeft size={16} />
            {t("bankOpportunities.sourceRegistry.back")}
          </button>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#A77E35]">
            Panatlantik Intelligence
          </p>
          <h1 className="font-serif text-3xl text-[#0B2545] md:text-4xl">
            {t("bankOpportunities.sourceRegistry.title")}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
            {t("bankOpportunities.sourceRegistry.subtitle")}
          </p>
        </div>

        {user?.role === "admin" && (
          <Button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            className="h-11 rounded-none bg-[#0B2545] px-5 hover:bg-[#12345c]"
          >
            <Plus size={18} className="mr-2" />
            {t("bankOpportunities.sourceRegistry.add")}
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Stat
          icon={Database}
          label={t("bankOpportunities.sourceRegistry.total")}
          value={totals.total}
        />
        <Stat
          icon={CheckCircle}
          label={t("bankOpportunities.sourceRegistry.activeSources")}
          value={totals.active}
        />
        <Stat
          icon={ShieldCheck}
          label={t("bankOpportunities.sourceRegistry.officialSources")}
          value={totals.official}
          gold
        />
      </div>

      <div className="grid gap-3 border border-slate-200 bg-white p-4 md:grid-cols-[1fr_230px_200px]">
        <div className="relative">
          <MagnifyingGlass
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("bankOpportunities.sourceRegistry.search")}
            className="h-11 rounded-none pl-10"
          />
        </div>
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          className={fieldClass}
        >
          <option value="all">
            {t("bankOpportunities.sourceRegistry.allCategories")}
          </option>
          {(meta?.categories || []).map((item) => (
            <option key={item} value={item}>
              {t(`bankOpportunities.sourceRegistry.categories.${item}`)}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className={fieldClass}
        >
          <option value="all">
            {t("bankOpportunities.sourceRegistry.allStatuses")}
          </option>
          {(meta?.statuses || []).map((item) => (
            <option key={item} value={item}>
              {t(
                `bankOpportunities.sourceRegistry.${statusKey[item] || item}`
              )}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#0B2545] border-t-transparent" />
        </div>
      ) : sources.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white py-20 text-center text-sm text-slate-500">
          {t("bankOpportunities.sourceRegistry.noResults")}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {sources.map((source) => (
            <article
              key={source.id}
              onClick={() => openEdit(source)}
              className={`border border-slate-200 bg-white p-6 transition-all ${
                user?.role === "admin"
                  ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg"
                  : ""
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="mb-2 flex flex-wrap gap-2">
                    {source.is_official && (
                      <span className="inline-flex items-center gap-1 bg-[#C5A059]/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#8A692D]">
                        <ShieldCheck size={13} weight="fill" />
                        {t("bankOpportunities.sourceRegistry.official")}
                      </span>
                    )}
                    <span className="bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                      {t(
                        `bankOpportunities.sourceRegistry.categories.${source.category}`
                      )}
                    </span>
                  </div>
                  <h2 className="font-serif text-2xl text-[#0B2545]">
                    {source.name}
                  </h2>
                  <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500">
                    {source.description}
                  </p>
                </div>
                {source.status === "active" ? (
                  <CheckCircle size={23} className="shrink-0 text-emerald-600" />
                ) : (
                  <PauseCircle size={23} className="shrink-0 text-amber-600" />
                )}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 text-xs">
                <div>
                  <div className="text-slate-400">
                    {t("bankOpportunities.sourceRegistry.status")}
                  </div>
                  <div className="mt-1 font-medium text-slate-700">
                    {t(
                      `bankOpportunities.sourceRegistry.${
                        statusKey[source.status] || source.status
                      }`
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-slate-400">
                    {t("bankOpportunities.sourceRegistry.lastChecked")}
                  </div>
                  <div className="mt-1 font-medium text-slate-700">
                    {formatDate(source.last_checked_at)}
                  </div>
                </div>
              </div>

              {source.official_url && (
                <a
                  href={source.official_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#A77E35] hover:text-[#0B2545]"
                >
                  {t("bankOpportunities.sourceRegistry.openOfficial")}
                  <ArrowSquareOut size={16} />
                </a>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="flex gap-3 border-l-2 border-[#C5A059] bg-[#0B2545]/[0.04] p-4 text-xs leading-5 text-slate-600">
        <ShieldCheck size={20} className="shrink-0 text-[#A77E35]" />
        {t("bankOpportunities.sourceRegistry.notice")}
      </div>

      {showForm && (
        <SourceForm
          source={editing}
          meta={meta}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={(payload) => saveMutation.mutate(payload)}
          saving={saveMutation.isPending}
          t={t}
        />
      )}
    </div>
  );
}
